/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    LanguageService,
    LogService,
    NavigationService,
    UserService,
    UtilsService
} from '../../../../../../core/services';
import { NamespacesApiService } from '../../../../shared/http';
import { nameValidation } from '../../../../../../core/directives';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-namespace',
    templateUrl: './add-edit-namespace.component.html',
})

export class AddEditNamespaceComponent extends BaseComponent {
    /**
     * Namespace FormGroup.
     */
    public namespaceForm: FormGroup;

    /**
     * Store the id of namespace being edited.
     */
    public editId: string;

    /**
     * Store the current organisation ID for use in the API calls.
     */
    private readonly organisationId: string;

    /**
     * Store a list of namespaces
     * @private
     */
    private namespaceList: any = [];

    /**
     * Initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        public navigationService: NavigationService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private namespaceApiService: NamespacesApiService,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.organisationId = this.userService.getUser('organisationId');

        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setUpPageData();
    }

    /**
     * Submit namespace.
     */
    public submitNamespace(): Observable<any> {
        return new Observable((observer) => {
            this.getApiCall()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => {
                        this.navigateToURL('cm/organisation/namespaces');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('AddEditNamespace', 'submitNamespace', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Setup namespace form.
     * @private
     */
    private setupFormGroup(): void {
        this.namespaceForm = this.formBuilder.group({
            id: [null], // <-- used on edit
            name: [null, [nameValidation, Validators.required]],
        });
        this.initialFormData = this.namespaceForm.value;
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.namespaceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    if (this.editId) {
                        this.utilsService.debounce('form', () => {
                            if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                                this.namespaceForm.markAsPristine();
                            }
                        });
                    } else {
                        // No editId or initialFormData; Add new form data; mark as dirty (fix button state on error)
                        this.namespaceForm.markAsDirty();
                    }
                }
            });

        const form = this.namespaceForm.get('name');
        form.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespaceLabel: string): void => {
                    this.utilsService.debounce('checkNamespace', () => {
                        if (namespaceLabel !== this.initialFormData.name) {
                            this.checkForDuplicateNamespace(namespaceLabel, form);
                        }
                    });
                }
            });
    }

    /**
     * Check for duplicate namespace label.
     * @param namespaceLabel
     * @param form
     * @private
     */
    private checkForDuplicateNamespace(namespaceLabel: string, form: AbstractControl): void {
        const matchName = this.namespaceList.find((item: {
            name: string;
            id: string;
        }) => item.name.toLowerCase() === namespaceLabel.toLowerCase().trim() && item.id !== this.editId);
        if (matchName) {
            form.setErrors({ 'duplicateNamespace': `${this.languageService.translate('duplicate_namespace')}` });
            form.markAsTouched();
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.namespaceApiService.getAll(this.organisationId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(data => {
                this.namespaceList = data;
                this.setUpPage();
            });
    }

    /**
     * Call the relevant API function.
     * @private
     */
    private getApiCall(): Observable<any> {
        const namespaceId: string = this.namespaceForm.get('id').value;
        const name: string = this.namespaceForm.get('name').value;
        const orgId: string = this.organisationId;

        return namespaceId ?
            this.namespaceApiService.edit(orgId, namespaceId, { name: name }) :
            this.namespaceApiService.create(orgId, { name: name });
    }

    /**
     * Set up the page based on id.
     * @private
     */
    private setUpPage(): void {
        this.editId = this.editId || this.route.snapshot.paramMap.get('id');
        if (this.editId) {
            const selectedNamespace = this.namespaceList.find((item: any) => item.id.toString() === this.editId);
            this.patchForm(selectedNamespace);
        }
    }

    /**
     * patch the selected data in the form.
     * @param selectedNamespace
     * @private
     */
    private patchForm(selectedNamespace: any): void {
        this.namespaceForm.patchValue(selectedNamespace, { emitEvent: false });
        this.initialFormData = this.namespaceForm.value;
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigate([url]).then().catch();
    }
}
